import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';

type NavigatorWithHistory = {
  push: (to: string, state?: any) => void;
  replace: (to: string, state?: any) => void;
};

type UseUnsavedChangesWarningOptions = {
  isDirty: boolean;
  onSave?: () => Promise<void> | void;
  isFormValid?: boolean;
};

type UseUnsavedChangesWarningReturn = {
  isModalOpen: boolean;
  handleStay: () => void;
  handleDiscard: () => void;
  handleSaveAndLeave: () => Promise<void>;
  canSaveAndLeave: boolean;
};

export const useUnsavedChangesWarning = ({
  isDirty,
  onSave,
  isFormValid = false,
}: UseUnsavedChangesWarningOptions): UseUnsavedChangesWarningReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  const shouldBlockRef = useRef(isDirty);
  const locationRef = useRef<ReturnType<typeof useLocation>>(null!);
  const blockingEntryAddedRef = useRef(false);

  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();

  useEffect(() => {
    shouldBlockRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (isDirty && !blockingEntryAddedRef.current) {
      window.history.pushState(
        { isFormDirtyBlock: true },
        '',
        window.location.pathname + window.location.search
      );
      blockingEntryAddedRef.current = true;
    }

    if (!isDirty) {
      blockingEntryAddedRef.current = false;
    }
  }, [isDirty]);

  useEffect(() => {
    const history = navigator as NavigatorWithHistory;

    const originalPush = history.push;
    const originalReplace = history.replace;

    history.push = (...args: Parameters<typeof originalPush>) => {
      if (!shouldBlockRef.current) {
        originalPush.apply(history, args);
        return;
      }

      setIsModalOpen(true);
      setPendingNavigation(() => () => {
        shouldBlockRef.current = false;
        blockingEntryAddedRef.current = false;
        originalPush.apply(history, args);
      });
    };

    history.replace = (...args: Parameters<typeof originalReplace>) => {
      if (!shouldBlockRef.current) {
        originalReplace.apply(history, args);
        return;
      }

      setIsModalOpen(true);
      setPendingNavigation(() => () => {
        shouldBlockRef.current = false;
        blockingEntryAddedRef.current = false;
        originalReplace.apply(history, args);
      });
    };

    return () => {
      history.push = originalPush;
      history.replace = originalReplace;
    };
  }, [navigator]);

  useEffect(() => {
    const handlePopState = () => {
      if (!shouldBlockRef.current) {
        return;
      }

      blockingEntryAddedRef.current = false;

      setIsModalOpen(true);
      setPendingNavigation(() => () => {
        shouldBlockRef.current = false;
        window.history.back();
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldBlockRef.current) {
        return;
      }

      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleStay = useCallback(() => {
    setIsModalOpen(false);
    setPendingNavigation(null);

    if (shouldBlockRef.current && !blockingEntryAddedRef.current) {
      window.history.pushState(
        { isFormDirtyBlock: true },
        '',
        window.location.pathname + window.location.search
      );
      blockingEntryAddedRef.current = true;
    }
  }, []);

  const handleDiscard = useCallback(() => {
    shouldBlockRef.current = false;
    blockingEntryAddedRef.current = false;
    setIsModalOpen(false);

    if (pendingNavigation) {
      pendingNavigation();
    }
    setPendingNavigation(null);
  }, [pendingNavigation]);

  const handleSaveAndLeave = useCallback(async () => {
    if (!onSave) {
      return;
    }

    try {
      await onSave();
      shouldBlockRef.current = false;
      blockingEntryAddedRef.current = false;
      setIsModalOpen(false);

      if (pendingNavigation) {
        pendingNavigation();
      }

      setPendingNavigation(null);
    } catch (error) {
      console.error('Save failed:', error);
    }
  }, [onSave, pendingNavigation]);

  return {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave: Boolean(onSave && isFormValid),
  };
};
